import { useEffect } from 'react';
import { Lock, RotateCcw, Clock, AlertCircle } from 'lucide-react';
import useCardPanelStore from '../../../store/cardPanelStore';
import useMetricsStore from '../../../store/metricsStore';
import PhaseBarChart from '../../metrics/PhaseBarChart';
import formatRelative, { formatAbsolute } from '../../../utils/formatRelative';
import { formatDuration, phaseLabel, phaseColor, startUnknownReason } from '../../../utils/metricsFormat';

function Stat({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-0.5">{label}</div>
      <div className="text-sm text-text-primary">{children}</div>
    </div>
  );
}

// Bloco "Fluxo" do painel do cartão (M6b) — consome GET /cards/:id/flow
// (M6a). Mesma permissão do Log de atividade (activity.read); por isso o
// tratamento de 403 segue o padrão de ActivityTab.jsx.
export default function FlowTab() {
  const openCardId = useCardPanelStore((s) => s.openCardId);
  const cardFlow = useMetricsStore((s) => s.cardFlow);
  const loadingCardFlow = useMetricsStore((s) => s.loadingCardFlow);
  const cardFlowError = useMetricsStore((s) => s.cardFlowError);
  const fetchCardFlow = useMetricsStore((s) => s.fetchCardFlow);

  useEffect(() => {
    if (openCardId) fetchCardFlow(openCardId);
  }, [openCardId, fetchCardFlow]);

  const data = openCardId ? cardFlow[openCardId] : null;
  const loading = openCardId ? loadingCardFlow[openCardId] : false;
  const error = openCardId ? cardFlowError[openCardId] : null;

  if (loading && !data) return <div className="p-6 text-sm text-text-secondary text-center">Carregando...</div>;

  if (error?.status === 403) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-full text-center">
        <Lock className="w-12 h-12 text-danger/40 mb-3" />
        <div className="text-sm font-semibold text-text-primary mb-1">Sem permissão para ver o fluxo deste item</div>
        <div className="text-xs text-text-secondary">Peça acesso a um administrador do quadro.</div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-sm text-danger text-center">{error.message}</div>;
  }

  if (!data) return null;

  const { coverage, cycles, phase_totals, open_interval } = data;
  const firstCycle = cycles[0];

  return (
    <div className="p-5 overflow-y-auto h-full space-y-5">
      {coverage.start_unknown && (
        <div className="flex items-start gap-2 bg-warning/10 border border-warning/30 rounded-lg px-3 py-2.5">
          <AlertCircle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
          <div className="text-xs text-text-primary">
            <div className="font-medium mb-0.5">Início desconhecido</div>
            <div className="text-text-secondary">{startUnknownReason(coverage.reason)}</div>
          </div>
        </div>
      )}

      {open_interval && (
        <div className="flex items-start gap-2 bg-accent/10 border border-accent/30 rounded-lg px-3 py-2.5">
          <Clock className="w-4 h-4 text-accent shrink-0 mt-0.5" />
          <div className="text-xs text-text-primary">
            <div className="font-medium mb-0.5">
              Em <span className={phaseColor(open_interval.phase).text}>{phaseLabel(open_interval.phase)}</span> há {formatDuration(open_interval.ms)}
            </div>
            <div className="text-text-secondary">
              Desde {formatAbsolute(open_interval.since)}
              {open_interval.assignee_name ? ` · Responsável: ${open_interval.assignee_name}` : ''}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Stat label="Iniciado em">
          {data.first_started_at
            ? <span title={formatAbsolute(data.first_started_at)}>{formatRelative(data.first_started_at)}</span>
            : '—'}
        </Stat>
        <Stat label="Responsável no início">
          {firstCycle?.start_assignee_name || '—'}
        </Stat>
        <Stat label="Concluído em">
          {data.first_completed_at
            ? <span title={formatAbsolute(data.first_completed_at)}>{formatRelative(data.first_completed_at)}</span>
            : 'Ainda não concluído'}
        </Stat>
        <Stat label="Reaberturas">
          {data.reopen_count > 0
            ? <span className="inline-flex items-center gap-1 text-warning"><RotateCcw className="w-3.5 h-3.5" />{data.reopen_count}</span>
            : '0'}
        </Stat>
        {data.lead_ms != null && (
          <Stat label="Lead time (criação até 1ª conclusão)">{formatDuration(data.lead_ms)}</Stat>
        )}
        {data.rework_ms > 0 && (
          <Stat label="Retrabalho após 1ª conclusão">{formatDuration(data.rework_ms)}</Stat>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-text-secondary font-semibold mb-2">Tempo por fase</div>
        <PhaseBarChart
          items={phase_totals.map((t) => ({ phase: t.phase, value: t.ms }))}
          formatValue={formatDuration}
        />
      </div>

      {cycles.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-secondary font-semibold mb-2">
            Ciclos{cycles.length > 1 ? ` (${cycles.length})` : ''}
          </div>
          <div className="space-y-2">
            {cycles.slice().reverse().map((c) => (
              <div key={c.cycle_no} className="bg-main border border-border/50 rounded-lg p-3 text-xs">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-medium text-text-primary">
                    Ciclo {c.cycle_no}{c.opened_by_reopen ? ' · reaberto' : ''}
                  </span>
                  {c.cycle_ms != null && <span className="text-text-secondary">{formatDuration(c.cycle_ms)}</span>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-text-secondary/70">Início</div>
                    <div className="text-text-primary">{c.started_at ? formatAbsolute(c.started_at) : '—'}</div>
                    {c.start_assignee_name && <div className="text-text-secondary">Resp.: {c.start_assignee_name}</div>}
                  </div>
                  <div>
                    <div className="text-text-secondary/70">Fim</div>
                    <div className="text-text-primary">{c.completed_at ? formatAbsolute(c.completed_at) : 'Em aberto'}</div>
                    {c.end_assignee_name && <div className="text-text-secondary">Resp.: {c.end_assignee_name}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
